import type { Action } from "@mailhand/core/schemas";
import type { ReactNode } from "react";
import { ACTION_MODE_LABEL, ACTION_TYPE_LABEL } from "../../app/labels.js";
import { Link } from "../../app/router.js";
import { useApi } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { Badge, Icon, Switch } from "../../ui/index.js";
import { specOf } from "./draft.js";

/** How a row opens its action: the edit page, or an editor the caller shows in place. */
export type ActionRowOpen = { kind: "page" } | { kind: "inline"; expanded: boolean; onToggle: () => void };

export function isSingleton(action: Action): boolean {
  return action.type === "hold" || action.type === "ignore";
}

/** The tenant's own actions first, then the built-in Hold and Ignore. */
export function orderedActions(actions: readonly Action[]): Action[] {
  return [...actions].sort((a, b) => Number(isSingleton(a)) - Number(isSingleton(b)));
}

export function ActionRow({ action, open }: { action: Action; open: ActionRowOpen }) {
  const api = useApi();
  const { actions, setActions } = useTenant();
  const task = useTask();
  const canToggle = action.type !== "hold";

  async function toggle(enabled: boolean): Promise<void> {
    const spec = specOf(action);
    if (spec.type === "hold") return;
    await task.run(async () => {
      const saved = await api.call("actionReplace", { params: { id: action.id }, body: { ...spec, enabled } });
      setActions(actions.map((existing) => (existing.id === saved.id ? saved : existing)));
    }, `${action.name} is ${enabled ? "on" : "off"}.`);
  }

  const summary: ReactNode = (
    <>
      <span className="action-name">
        {isSingleton(action) ? <Icon name="lock" size={13} className="muted" /> : null}
        {action.name}
      </span>
      <span className="action-description">{action.description}</span>
    </>
  );

  return (
    <li className={`action-row${action.enabled ? "" : " is-off"}`}>
      {open.kind === "page" ? (
        <Link to={`/actions/${action.id}`} className="action-main">
          {summary}
        </Link>
      ) : (
        <button type="button" className="action-main action-main-button" aria-expanded={open.expanded} onClick={open.onToggle}>
          {summary}
        </button>
      )}
      <span className="action-badges">
        <Badge tone="outline">{ACTION_TYPE_LABEL[action.type]}</Badge>
        {action.followUp ? <Badge tone="blue">Follow-up</Badge> : null}
        {action.type === "hold" || action.type === "ignore" ? null : (
          <>
            <Badge tone={action.mode === "auto" ? "green" : "amber"}>{ACTION_MODE_LABEL[action.mode]}</Badge>
            <span className="action-cap mono" title="Daily limit">
              {action.dailyCap}
              <span className="unit">/day</span>
            </span>
          </>
        )}
      </span>
      <span className="action-toggle">
        {canToggle ? <Switch compact checked={action.enabled} disabled={task.busy} onChange={(enabled) => void toggle(enabled)} label={`${action.name} enabled`} /> : <span className="muted small">Always on</span>}
      </span>
    </li>
  );
}
